import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchTeacherKpis, fetchTeachersPage } from '@services/registry';

export function useTeachers({ tenantId, page = 1, pageSize = 10, includeRows = true }) {
    const cursorByPageRef = useRef(new Map([[1, null]]));

    useEffect(() => {
        cursorByPageRef.current = new Map([[1, null]]);
    }, [tenantId, pageSize]);

    const teachersQuery = useQuery({
        queryKey: ['teachers', tenantId, page, pageSize],
        queryFn: async () => {
            const cursor = cursorByPageRef.current.get(page);
            if (page > 1 && typeof cursor === 'undefined') {
                throw new Error('Pagination cursor missing. Please go page-by-page.');
            }
            const res = await fetchTeachersPage({
                tenantId,
                pageSize,
                cursor: cursor ?? null,
            });
            cursorByPageRef.current.set(page + 1, res.nextCursor || null);
            return res;
        },
        enabled: !!tenantId && includeRows,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const kpisQuery = useQuery({
        queryKey: ['teacherKpis', tenantId],
        queryFn: () => fetchTeacherKpis({ tenantId }),
        enabled: !!tenantId,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    return {
        rows: includeRows ? (teachersQuery.data?.rows || []) : [],
        total: includeRows ? (teachersQuery.data?.total || 0) : (kpisQuery.data?.totalTeachers || 0),
        kpis: kpisQuery.data || {
            totalTeachers: 0,
            activeTeachers: 0,
            inactiveTeachers: 0,
            deletedTeachers: 0,
        },
        loading: includeRows ? (teachersQuery.isLoading || kpisQuery.isLoading) : kpisQuery.isLoading,
        error: teachersQuery.error || kpisQuery.error || null,
    };
}

